import * as signalR from "@microsoft/signalr";
import HttpStatus from "@cayo/shared/api/types/HttpStatus";
import { appEvents } from "@cayo/shared/components/App/appEvents";
import { NetworkMessages } from "@cayo/shared/i18n/messages/network-messages";
import logger, { ILogger } from "@cayo/shared/libs/logger";
import { endpoints } from "@cayo/shared/services/endpoints.service";
import { ServiceFactory } from "@cayo/shared/services/factory.service";
import tokenStorage from "@cayo/shared/settings/tokenStorage";
import { objectUtils } from "@cayo/shared/utils/object";
import { INotificationMessage, INotificationSubscription } from "./notification.types";
import { createSignalRConnection, RECONNECT_TIMEOUT } from "./signalr-connection";

export class NotificationService {
  private log: ILogger;
  constructor() {
    this.log = logger.getLogger("NotificationService");
    this.log.debug("NotificationService ctor");
  }

  private notificationsConnection: signalR.HubConnection | null = null;
  private subscriptions: INotificationSubscription[] = [];

  private get isServiceDisabled() {
    return localStorage.getItem("notifications") === "off";
  }

  public start = () => {
    this.log.debug("start");

    if (this.isServiceDisabled) {
      this.log.debug("exit: service disabled");
      return;
    }

    if (!tokenStorage.accessToken) {
      this.log.debug("no access token found, start reconnect");
      setTimeout(() => this.start(), RECONNECT_TIMEOUT);
      return;
    }

    let connection = createSignalRConnection();

    connection.on("OnMessages", this.onNotification);

    async function startConnection() {
      let connectionEstablished = false;
      try {
        const serviceStatus = await ServiceFactory.HealthCheckService.getReadyStatus();
        const apiState = serviceStatus?.results?.service?.data?.apiState;
        const newState = apiState?.state;
        if (newState !== "started") {
          appEvents.trigger({
            serverError: apiState,
          });
          throw new Error("server is not ready");
        }

        if (tokenStorage?.accessToken) {
          await connection.start();
          connectionEstablished = true;
        }

        appEvents.trigger({ webSocketConnectionLost: false });
      } catch (err: any) {
        // if (err?.statusCode === 401 || err?.errorType) {
        //   log.debug("reload page", err);
        //   document.location.reload();
        //   return;
        // }

        if (err.status === HttpStatus.NOT_FOUND) {
          appEvents.trigger({ serverError: { message: "", state: "stopped" } });
        }

        if (err.status === HttpStatus.UNAUTHORIZED) {
          connection = createSignalRConnection();
        }

        if (err.status === HttpStatus.SERVICE_UNAVAILABLE) {
          appEvents.trigger({
            serverError: {
              message: NetworkMessages.serverUnavailable.defaultMessage,
              state: "stopped",
            },
          });
        }

        if (err.status === HttpStatus.GATEWAY_TIMEOUT) {
          appEvents.trigger({
            serverError: {
              message: NetworkMessages.gatewayTimeout.defaultMessage,
              state: "stopped",
            },
          });
        }
      } finally {
        if (!connectionEstablished) {
          setTimeout(() => startConnection(), RECONNECT_TIMEOUT);
        }
      }
    }

    connection.onclose(async () => {
      appEvents.trigger({ webSocketConnectionLost: true });
      if (this.notificationsConnection) {
        await startConnection();
      }
    });

    this.notificationsConnection = connection;

    return connection
      .start()
      .then(() => {
        this.log.log("connection established with success, hub: " + endpoints.publicUrl);
      })
      .catch(() => {
        setTimeout(() => startConnection(), RECONNECT_TIMEOUT);
      });
  };

  public stop = () => {
    if (this.notificationsConnection) {
      const oldConnection = this.notificationsConnection;
      this.notificationsConnection = null;
      oldConnection.stop();
    }
  };

  public subscribe(source: string, handler: (messages: INotificationMessage[]) => void) {
    this.subscriptions.push({ source: source.toLowerCase(), handler });

    return () => {
      this.unsubscribe(handler);
    };
  }

  private unsubscribe = (handler: (messages: INotificationMessage[]) => void) => {
    const existingSubscriptionIndex = this.subscriptions.findIndex((s) => s.handler === handler);

    if (existingSubscriptionIndex >= 0) {
      this.subscriptions.splice(existingSubscriptionIndex, 1);
    }
  };

  private onNotification = (body: any) => {
    this.log.debug("onNotification -> body", body);
    const messages = body as INotificationMessage[];
    if (messages.length > 0 && this.subscriptions.length > 0) {
      const messageGroup = objectUtils.groupBy(messages, (m) => m.source.toLowerCase());
      this.log.debug("onNotification -> messageGroup ", messageGroup);
      for (const group of messageGroup) {
        const subscribers = this.subscriptions.filter(
          (s) => s.source.toLowerCase() === group[0].toLowerCase()
        );
        this.log.debug("onNotification subscribers.length -> ", subscribers.length);
        if (subscribers.length > 0) {
          const newMessages = group[1];

          for (const subscriber of subscribers) {
            subscriber.handler(newMessages);
          }
        }
      }
    }
  };
}
